import { type ComponentProps, type ComponentType, createElement, lazy, useState } from "react";

const RELOAD_KEY = "ow-chunk-reload-at";
const RELOAD_WINDOW_MS = 15_000;

/** Reload the page once for a failed chunk load. A build that no longer
    exists on the server can never be fetched, so the only recovery is a fresh
    index.html; the timestamp guard stops a broken deployment from reloading
    in a loop. Returns false when a reload was attempted too recently. */
export function reloadOnceForChunkError(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  } catch {}
  const now = Date.now();
  if (now - last < RELOAD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {}
  window.location.reload();
  return true;
}

/** `React.lazy` whose loader survives a deploy: when the chunk it names is
    gone (the server has a new build with new hashes), reload the page once
    instead of rejecting into the render tree. */
// biome-ignore lint/suspicious/noExplicitAny: mirrors React.lazy's own ComponentType<any> bound
export function lazyRetry<T extends ComponentType<any>>(loader: () => Promise<{ default: T }>) {
  let resolved: T | null = null;
  let pending: Promise<{ default: T }> | null = null;
  const load = () => {
    pending ??= loader().then(
      (mod) => {
        resolved = mod.default;
        return mod;
      },
      (err: unknown) => {
        pending = null;
        throw err;
      },
    );
    return pending;
  };
  const Lazy = lazy(() =>
    load().catch((err: unknown) => {
      if (reloadOnceForChunkError()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
  function Preloadable(props: ComponentProps<T>) {
    const [Ready] = useState(() => resolved);
    return createElement((Ready ?? Lazy) as ComponentType<ComponentProps<T>>, props);
  }
  return Object.assign(Preloadable, { preload: (): Promise<unknown> => load() });
}
